import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Search, BarChart3, Sparkles, ExternalLink, CheckCircle2, AlertTriangle,
  Trash2, Loader2, KeyRound, Eye, EyeOff,
} from "lucide-react";

/**
 * Per-client Microsoft / Bing integrations:
 *   1. Bing Webmaster Tools (API key)
 *   2. Microsoft Advertising / Bing Ads (OAuth + Developer Token)
 *   3. Microsoft Clarity (bearer token + project_id)
 *
 * Each service has its own connect card. The catalog of metrics exposed by
 * each service is rendered as a read-only preview underneath.
 */
export default function ClientMicrosoftPage() {
  const { clientId } = useParams();
  const [catalog, setCatalog] = useState(null);

  const [bw, setBw] = useState({ connected: false });
  const [ads, setAds] = useState({ connected: false });
  const [clarity, setClarity] = useState({ connected: false });

  // ---- form state ----
  const [bwKey, setBwKey] = useState("");
  const [bwShow, setBwShow] = useState(false);
  const [bwBusy, setBwBusy] = useState(false);

  const [adsBusy, setAdsBusy] = useState(false);

  const [clarityToken, setClarityToken] = useState("");
  const [clarityProj, setClarityProj] = useState("");
  const [clarityShow, setClarityShow] = useState(false);
  const [clarityBusy, setClarityBusy] = useState(false);

  const refresh = async () => {
    try {
      const [cat, b, a, c] = await Promise.all([
        api.get("/microsoft/field-catalog").then((r) => r.data).catch(() => null),
        api.get(`/clients/${clientId}/bing-webmaster/connection`).then((r) => r.data).catch(() => ({})),
        api.get(`/clients/${clientId}/microsoft-ads/connection`).then((r) => r.data).catch(() => ({})),
        api.get(`/clients/${clientId}/clarity/connection`).then((r) => r.data).catch(() => ({})),
      ]);
      setCatalog(cat);
      setBw(b); setAds(a); setClarity(c);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { refresh(); }, [clientId]);

  // ---- Bing Webmaster actions ----
  const connectBingWebmaster = async (e) => {
    e?.preventDefault?.();
    if (!bwKey.trim()) { toast.error("API key required"); return; }
    setBwBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/bing-webmaster/connect`,
        { api_key: bwKey.trim() });
      toast.success(`Connected · ${data.sites} site${data.sites === 1 ? "" : "s"}`);
      setBwKey(""); await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection failed");
    } finally { setBwBusy(false); }
  };
  const disconnectBingWebmaster = async () => {
    if (!window.confirm("Disconnect Bing Webmaster?")) return;
    await api.delete(`/clients/${clientId}/bing-webmaster/disconnect`);
    toast.success("Disconnected"); await refresh();
  };

  // ---- Microsoft Ads (OAuth) ----
  const connectMicrosoftAds = async () => {
    setAdsBusy(true);
    try {
      const redirectUri = `${window.location.origin}/microsoft-ads-oauth-callback`;
      const { data } = await api.post(`/clients/${clientId}/microsoft-ads/oauth/start`,
        { redirect_uri: redirectUri });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start Microsoft Ads OAuth");
    } finally { setAdsBusy(false); }
  };
  const disconnectMicrosoftAds = async () => {
    if (!window.confirm("Disconnect Microsoft Advertising?")) return;
    await api.delete(`/clients/${clientId}/microsoft-ads/disconnect`);
    toast.success("Disconnected"); await refresh();
  };

  // ---- Clarity actions ----
  const connectClarity = async (e) => {
    e?.preventDefault?.();
    if (!clarityToken.trim() || !clarityProj.trim()) {
      toast.error("Both API token and Project ID required"); return;
    }
    setClarityBusy(true);
    try {
      await api.post(`/clients/${clientId}/clarity/connect`,
        { api_token: clarityToken.trim(), project_id: clarityProj.trim() });
      toast.success("Clarity connected");
      setClarityToken(""); setClarityProj(""); await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Connection failed");
    } finally { setClarityBusy(false); }
  };
  const disconnectClarity = async () => {
    if (!window.confirm("Disconnect Microsoft Clarity?")) return;
    await api.delete(`/clients/${clientId}/clarity/disconnect`);
    toast.success("Disconnected"); await refresh();
  };

  const services = useMemo(() => {
    if (!catalog) return [];
    return Object.entries(catalog).map(([key, v]) => ({ key, ...v }));
  }, [catalog]);

  return (
    <div className="space-y-6" data-testid="microsoft-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 grid place-items-center rounded-sm bg-[#008373] text-white">
          <Search className="h-5 w-5" />
        </div>
        <div>
          <div className="label-overline">MICROSOFT · BING</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Microsoft suite</h1>
          <p className="text-xs text-zinc-600 mt-1 max-w-2xl">
            Connect Bing Webmaster Tools (search console), Microsoft Advertising
            (Bing Ads), and Microsoft Clarity (the Microsoft equivalent of Google
            Analytics). See{" "}
            <a href="/docs/microsoft_setup.md" className="underline" target="_blank" rel="noreferrer">
              docs/microsoft_setup.md
            </a>{" "}
            for credential instructions.
          </p>
        </div>
      </div>

      {/* Setup banner */}
      <Card className="rounded-sm border-amber-200 bg-amber-50" data-testid="ms-setup-banner">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-[12px] text-amber-900 flex-1">
            <div className="font-semibold mb-0.5">What you need before connecting</div>
            <ul className="list-disc pl-4 mt-1 space-y-0.5">
              <li><b>Bing Webmaster</b>: API key from{" "}
                <a href="https://www.bing.com/webmasters/api"
                  target="_blank" rel="noreferrer" className="underline">
                  bing.com/webmasters/api <ExternalLink className="inline h-3 w-3" />
                </a>.</li>
              <li><b>Microsoft Advertising</b>: Azure Entra app (Client ID + Secret) +
                Developer Token from{" "}
                <a href="https://developers.ads.microsoft.com/Account"
                  target="_blank" rel="noreferrer" className="underline">
                  developers.ads.microsoft.com <ExternalLink className="inline h-3 w-3" />
                </a>. Set <span className="font-mono">MICROSOFT_ADS_CLIENT_ID</span>,
                <span className="font-mono ml-1">MICROSOFT_ADS_CLIENT_SECRET</span>,
                <span className="font-mono ml-1">MICROSOFT_ADS_DEVELOPER_TOKEN</span> in <code>backend/.env</code>.</li>
              <li><b>Microsoft Clarity</b>: API token from <code>clarity.microsoft.com</code> → Settings → Data Export, plus the Project ID.</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* === Bing Webmaster === */}
      <ServiceCard
        testid="bw"
        accent="#008373"
        icon={<Search className="h-5 w-5" />}
        title="Bing Webmaster Tools"
        subtitle="Search clicks, impressions, queries and crawl data — Bing's search console."
        connected={bw.connected}
        statusLabel={bw.connected ? `Connected · ${(bw.sites || []).length} site${(bw.sites || []).length === 1 ? "" : "s"}` : null}
        onDisconnect={disconnectBingWebmaster}
        disconnectTestid="bw-disconnect"
      >
        {!bw.connected ? (
          <form onSubmit={connectBingWebmaster} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="label-overline">Bing Webmaster API key</Label>
              <div className="flex gap-2">
                <Input
                  type={bwShow ? "text" : "password"}
                  value={bwKey} onChange={(e) => setBwKey(e.target.value)}
                  placeholder="Paste the key from bing.com/webmasters/api"
                  className="rounded-sm font-mono text-xs flex-1"
                  data-testid="bw-key-input"
                />
                <Button type="button" variant="outline" className="rounded-sm" size="sm"
                  onClick={() => setBwShow((s) => !s)}>
                  {bwShow ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>
            <Button type="submit" disabled={bwBusy}
              className="rounded-sm bg-[#008373] hover:bg-[#006658] text-white"
              data-testid="bw-connect">
              {bwBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <KeyRound className="h-3.5 w-3.5 mr-1.5" />}
              Connect
            </Button>
          </form>
        ) : (
          <div className="space-y-2">
            <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Active
            </Badge>
            {(bw.sites || []).slice(0, 8).map((s, i) => (
              <div key={s || `bw-site-${i}`} className="font-mono text-[11px] text-zinc-600 truncate"
                data-testid={`bw-site-${i}`}>{s}</div>
            ))}
          </div>
        )}
      </ServiceCard>

      {/* === Microsoft Advertising === */}
      <ServiceCard
        testid="ads"
        accent="#0078D4"
        icon={<BarChart3 className="h-5 w-5" />}
        title="Microsoft Advertising"
        subtitle="Bing Ads campaigns, keywords, conversions and audience insights."
        connected={ads.connected}
        statusLabel={ads.connected ? "Connected via OAuth" : null}
        onDisconnect={disconnectMicrosoftAds}
        disconnectTestid="ads-disconnect"
      >
        {!ads.connected ? (
          <Button onClick={connectMicrosoftAds} disabled={adsBusy}
            className="rounded-sm bg-[#0078D4] hover:bg-[#005a9e] text-white"
            data-testid="ads-connect">
            {adsBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
            Connect with Microsoft
          </Button>
        ) : (
          <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Token stored encrypted server-side
          </Badge>
        )}
      </ServiceCard>

      {/* === Microsoft Clarity === */}
      <ServiceCard
        testid="clarity"
        accent="#5C2D91"
        icon={<Sparkles className="h-5 w-5" />}
        title="Microsoft Clarity"
        subtitle="Sessions, scroll depth, dead/rage clicks, heatmaps and recordings."
        connected={clarity.connected}
        statusLabel={clarity.connected ? `Project · ${clarity.project_id}` : null}
        onDisconnect={disconnectClarity}
        disconnectTestid="clarity-disconnect"
      >
        {!clarity.connected ? (
          <form onSubmit={connectClarity} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="label-overline">API token</Label>
                <div className="flex gap-2">
                  <Input
                    type={clarityShow ? "text" : "password"}
                    value={clarityToken} onChange={(e) => setClarityToken(e.target.value)}
                    placeholder="Paste from clarity.microsoft.com"
                    className="rounded-sm font-mono text-xs flex-1"
                    data-testid="clarity-token-input"
                  />
                  <Button type="button" variant="outline" className="rounded-sm" size="sm"
                    onClick={() => setClarityShow((s) => !s)}>
                    {clarityShow ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="label-overline">Project ID</Label>
                <Input value={clarityProj} onChange={(e) => setClarityProj(e.target.value)}
                  placeholder="e.g. xyz123abc"
                  className="rounded-sm font-mono text-xs"
                  data-testid="clarity-project-input"
                />
              </div>
            </div>
            <Button type="submit" disabled={clarityBusy}
              className="rounded-sm bg-[#5C2D91] hover:bg-[#46226e] text-white"
              data-testid="clarity-connect">
              {clarityBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <KeyRound className="h-3.5 w-3.5 mr-1.5" />}
              Connect
            </Button>
          </form>
        ) : (
          <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Token stored encrypted server-side
          </Badge>
        )}
      </ServiceCard>

      {/* === Catalog preview === */}
      {services.length > 0 && (
        <Card className="rounded-sm border-dashed border-zinc-300 bg-zinc-50/40"
          data-testid="ms-catalog-preview">
          <CardContent className="p-5">
            <div className="label-overline mb-3">Available metrics once connected</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[12px]">
              {services.map((s) => (
                <div key={s.key} className="rounded-sm border border-zinc-200 bg-white p-3">
                  <div className="font-semibold mb-1.5">{s.label}</div>
                  <ul className="text-zinc-600 space-y-0.5">
                    {(s.groups || []).map((g) => (
                      <li key={g.label} className="font-mono text-[11px]">
                        · {g.label} <span className="text-zinc-400">({g.fields.length})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ServiceCard({
  testid, accent, icon, title, subtitle, connected, statusLabel,
  onDisconnect, disconnectTestid, children,
}) {
  return (
    <Card className="rounded-sm border-zinc-200" data-testid={`ms-service-${testid}`}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 grid place-items-center rounded-sm text-white shrink-0"
              style={{ backgroundColor: accent }}>{icon}</div>
            <div>
              <div className="font-display font-bold text-lg">{title}</div>
              <div className="text-xs text-zinc-500 max-w-md">{subtitle}</div>
              {statusLabel && (
                <div className="text-[11px] text-emerald-700 font-mono mt-1">{statusLabel}</div>
              )}
            </div>
          </div>
          {connected && onDisconnect && (
            <Button size="sm" variant="ghost" className="rounded-sm text-red-600"
              onClick={onDisconnect} data-testid={disconnectTestid}>
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Disconnect
            </Button>
          )}
        </div>
        <div className="pt-3 border-t border-zinc-100">
          {children}
        </div>
      </CardContent>
    </Card>
  );
}
